import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Circle } from '@phosphor-icons/react';
import { toast } from 'sonner';

import pt from '../../locales/pt';
import { Button } from '../ui/Button';
import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { Slider } from '../ui/Slider';
import { cn } from '../../utils/cn';
import {
  useActiveMethodQuery,
  useCreateBudgetMethodMutation,
  usePresetsQuery,
} from '../../features/budgets/queries';

export function BudgetMethodSheet({ isOpen, onClose }) {
  const { data: activeMethod } = useActiveMethodQuery({ enabled: isOpen });
  const { data: presets = [] } = usePresetsQuery({ enabled: isOpen });
  const createMethod = useCreateBudgetMethodMutation();

  const [mode, setMode] = useState('edit');
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [percentages, setPercentages] = useState([]);

  const groups = useMemo(() => {
    if (mode === 'preset' && selectedPresetId) {
      const preset = presets.find((p) => p.id === selectedPresetId);
      return preset?.groups || [];
    }
    return activeMethod?.groups || [];
  }, [mode, selectedPresetId, presets, activeMethod]);

  useEffect(() => {
    if (!isOpen) return;
    setMode('edit');
    setSelectedPresetId(null);
    setPercentages((activeMethod?.groups || []).map((g) => Number(g.target_percentage)));
  }, [isOpen, activeMethod]);

  useEffect(() => {
    if (mode === 'preset' && selectedPresetId) {
      const preset = presets.find((p) => p.id === selectedPresetId);
      setPercentages((preset?.groups || []).map((g) => Number(g.target_percentage)));
    }
  }, [mode, selectedPresetId, presets]);

  const total = useMemo(
    () => percentages.reduce((sum, value) => sum + value, 0),
    [percentages],
  );
  const isValid = total === 100 && groups.length > 0;

  const onSliderChange = (index, [value]) => {
    setPercentages((current) =>
      current.map((existing, idx) => (idx === index ? value : existing)),
    );
  };

  const onSave = async () => {
    if (!isValid) return;
    const presetId =
      mode === 'preset' && selectedPresetId
        ? selectedPresetId
        : activeMethod?.preset_id || 'fifty_thirty_twenty';

    try {
      await createMethod.mutateAsync({
        preset_id: presetId,
        customisations: groups.map((group, index) => ({
          name: group.name,
          color: group.color,
          icon: group.icon,
          target_percentage: Number(percentages[index]).toFixed(2),
        })),
      });
      toast.success(pt.budget.method.changeSuccess);
      onClose();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : pt.budget.method.changeError);
    }
  };

  const footer = (
    <div className="flex justify-end gap-space-2">
      <Button type="button" variant="ghost" onClick={onClose}>
        {pt.common.cancel}
      </Button>
      <Button
        type="button"
        onClick={onSave}
        disabled={!isValid || createMethod.isPending}
      >
        {createMethod.isPending ? pt.common.loading : pt.common.save}
      </Button>
    </div>
  );

  return (
    <BottomSheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <BottomSheetContent
        title={pt.budget.method.changeTitle}
        description={pt.budget.method.changeDescription}
        footer={footer}
      >
        <div className="flex flex-col gap-space-4">
          <div className="flex w-full gap-space-1 rounded-ds-full bg-muted p-space-1">
            <button
              type="button"
              onClick={() => setMode('edit')}
              className={cn(
                'type-body-sm flex-1 rounded-ds-full py-space-2 text-center font-semibold transition-colors',
                mode === 'edit' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
              )}
            >
              {pt.budget.method.editCurrent}
            </button>
            <button
              type="button"
              onClick={() => setMode('preset')}
              className={cn(
                'type-body-sm flex-1 rounded-ds-full py-space-2 text-center font-semibold transition-colors',
                mode === 'preset' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
              )}
            >
              {pt.budget.method.choosePreset}
            </button>
          </div>

          {mode === 'preset' ? (
            <div className="flex flex-col gap-space-3">
              {presets.map((preset) => {
                const selected = selectedPresetId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setSelectedPresetId(preset.id)}
                    className={cn(
                      'flex flex-col gap-space-2 rounded-ds-md border p-space-4 text-left transition-colors',
                      selected
                        ? 'border-2 border-sage-500 bg-sage-tint'
                        : 'border-border bg-card hover:bg-muted',
                    )}
                  >
                    <span className="flex items-center gap-space-2">
                      <span className="type-body min-w-0 flex-1 font-semibold text-foreground">
                        {preset.name}
                      </span>
                      {selected ? (
                        <CheckCircle className="h-[17px] w-[17px] shrink-0 text-sage-on-tint" aria-hidden />
                      ) : (
                        <Circle className="h-[17px] w-[17px] shrink-0 text-input-border" aria-hidden />
                      )}
                    </span>
                    <p className="type-body-sm font-normal text-muted-foreground">{preset.tagline}</p>
                  </button>
                );
              })}
            </div>
          ) : null}

          {groups.length > 0 ? (
            <div className="flex flex-col gap-space-4">
              <p className="type-label font-semibold text-foreground">
                {pt.budget.method.adjustPercentages}
              </p>
              {groups.map((group, index) => (
                <div key={group.name || index} className="flex flex-col gap-space-3">
                  <div className="flex items-center justify-between">
                    <span className="type-label font-semibold text-foreground">{group.name}</span>
                    <span
                      className="type-display-row font-semibold"
                      style={{ color: group.color || undefined }}
                    >
                      {percentages[index]}%
                    </span>
                  </div>
                  <Slider
                    value={[percentages[index] ?? 0]}
                    min={0}
                    max={100}
                    step={1}
                    onValueChange={(value) => onSliderChange(index, value)}
                  />
                </div>
              ))}
              <div className="flex items-center justify-between gap-space-2 rounded-ds-md bg-sage-tint px-space-4 py-space-3">
                <span className="flex items-center gap-space-2">
                  <CheckCircle className="h-4 w-4 shrink-0 text-sage-on-tint" aria-hidden />
                  <span className="type-label font-semibold text-sage-on-tint">
                    {pt.budget.method.totalCheckLabel}
                  </span>
                </span>
                <span
                  className={cn(
                    'type-display-row font-semibold',
                    isValid ? 'text-sage-on-tint' : 'text-warning-fg',
                  )}
                >
                  {total}%
                </span>
              </div>
            </div>
          ) : (
            <p className="type-body-sm font-normal text-muted-foreground">{pt.budget.method.empty}</p>
          )}
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
}
